import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { requirementsApi } from '../../../services/api';
import './review.css';

/**
 * F-62 / REQ-6120 (#606, #629): the admin surface for review assignment / delegation
 * — `POST /items/{id}/review-assignment`, `GET /items/{id}/review-assignments`,
 * `POST /review-assignments/{id}/delegate`. Before this, the REST routes existed
 * with no way to reach them from the app at all.
 *
 * Mounted LAZILY (ItemBlock only renders this once its toggle is opened) — a
 * Breakdown subtree can hold hundreds of items, and firing one GET per item on
 * mount would be the exact kind of unbounded per-row fetch #421's comment-count
 * endpoint was introduced to avoid.
 *
 * `assign_review` supersedes whatever was pending, so there is at most ONE current
 * assignment; `include_history=true` additionally returns the full delegation
 * chain (superseded rows included) for "who was asked before this".
 */
function ReviewAssignmentPanel({ itemId, currentUserId }) {
  const [assignments, setAssignments] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState(null);
  const [assigneeInput, setAssigneeInput] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [delegating, setDelegating] = useState(false);
  const [delegateTo, setDelegateTo] = useState('');
  const [delegateNote, setDelegateNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await requirementsApi.listReviewAssignments(itemId, false);
      setAssignments(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (err) {
      setError(`Could not load review assignment: ${err.message}`);
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await requirementsApi.listReviewAssignments(itemId, true);
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(`Could not load assignment history: ${err.message}`);
    }
  }, [itemId]);

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadHistory();
  };

  const current = (assignments || [])[0] || null;
  const iAmAssignee = !!(current && currentUserId && current.assignee === currentUserId);

  const handleAssign = async () => {
    const assignee = assigneeInput.trim();
    if (!assignee) return;
    setBusy(true);
    try {
      await requirementsApi.assignReview(itemId, assignee, assignNote.trim());
      setAssigneeInput('');
      setAssignNote('');
      setError(null);
      await load();
      if (showHistory) await loadHistory();
    } catch (err) {
      setError(`Could not assign review: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelegate = async () => {
    const to = delegateTo.trim();
    if (!to || !current) return;
    setBusy(true);
    try {
      await requirementsApi.delegateReview(current.assignment_id, to, delegateNote.trim());
      setDelegating(false);
      setDelegateTo('');
      setDelegateNote('');
      setError(null);
      await load();
      if (showHistory) await loadHistory();
    } catch (err) {
      setError(`Could not delegate review: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rmdv-assign" data-testid={`review-assignment-${itemId}`}>
      {current ? (
        <span className="rmdv-assign-current" data-testid={`review-assignee-${itemId}`}>
          {'Review assigned to '}
          <b>{current.assignee}</b>
          {current.delegated_from ? ` (delegated from ${current.delegated_from})` : ''}
        </span>
      ) : (
        <span className="rmdv-assign-current muted3" data-testid={`review-assignee-${itemId}`}>
          {assignments === null ? 'Loading…' : 'No pending review assignment'}
        </span>
      )}

      {iAmAssignee && !delegating && (
        <button
          type="button"
          className="linkbtn"
          onClick={() => setDelegating(true)}
          data-testid={`delegate-start-${itemId}`}
        >
          Delegate
        </button>
      )}

      {iAmAssignee && delegating && (
        <span className="rmdv-assign-form">
          <input
            className="rmdv-assign-input"
            placeholder="Delegate to (user id)"
            value={delegateTo}
            onChange={(e) => setDelegateTo(e.target.value)}
            data-testid={`delegate-to-input-${itemId}`}
          />
          <input
            className="rmdv-assign-input"
            placeholder="Note (optional)"
            value={delegateNote}
            onChange={(e) => setDelegateNote(e.target.value)}
            data-testid={`delegate-note-input-${itemId}`}
          />
          <button
            type="button"
            className="linkbtn"
            disabled={busy || !delegateTo.trim()}
            onClick={handleDelegate}
            data-testid={`delegate-confirm-${itemId}`}
          >
            Confirm
          </button>
          <button type="button" className="linkbtn muted" onClick={() => setDelegating(false)}>
            Cancel
          </button>
        </span>
      )}

      <span className="rmdv-assign-form">
        <input
          className="rmdv-assign-input"
          placeholder="Assign to (user id)"
          value={assigneeInput}
          onChange={(e) => setAssigneeInput(e.target.value)}
          data-testid={`assign-to-input-${itemId}`}
        />
        <input
          className="rmdv-assign-input"
          placeholder="Note (optional)"
          value={assignNote}
          onChange={(e) => setAssignNote(e.target.value)}
          data-testid={`assign-note-input-${itemId}`}
        />
        <button
          type="button"
          className="linkbtn"
          disabled={busy || !assigneeInput.trim()}
          onClick={handleAssign}
          data-testid={`assign-confirm-${itemId}`}
        >
          Assign
        </button>
      </span>

      <button
        type="button"
        className="linkbtn muted"
        onClick={toggleHistory}
        data-testid={`assign-history-toggle-${itemId}`}
      >
        {showHistory ? 'Hide delegation history' : 'Show delegation history'}
      </button>

      {showHistory && (
        <ul className="rmdv-assign-history" data-testid={`assign-history-${itemId}`}>
          {history === null && <li className="muted3">Loading…</li>}
          {history !== null && history.length === 0 && (
            <li className="muted3">No assignment history.</li>
          )}
          {(history || []).map((h) => (
            <li key={h.assignment_id}>
              {`${h.assignee} — ${h.status}`}
              {h.delegated_from ? ` (delegated from ${h.delegated_from})` : ''}
              {h.note ? ` — ${h.note}` : ''}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div className="rmdv-assign-error" data-testid={`assign-error-${itemId}`}>{error}</div>
      )}
    </div>
  );
}

ReviewAssignmentPanel.propTypes = {
  itemId: PropTypes.string.isRequired,
  currentUserId: PropTypes.string,
};

export default ReviewAssignmentPanel;
